/**
 * Syncs top-level modal visibility (search, notifications, queue) with the
 * `modal` query parameter so that browser back / forward works.
 */
import { useRouter } from 'next/router'
import { useRecoilState } from 'recoil'
import { useEffect, useMemo, useRef } from 'react'
import { isSearchBoxOpenAtom } from '@/atoms/isSearchBoxOpen'
import {
    isNotificationsModalVisibleAtom,
    isNotificationQueueModalVisibleAtom,
} from '@/atoms/notifications'

export type ModalQueryValue = 'search' | 'notifications' | 'queue'

export const useModalUrlSync = (): void => {
    const router = useRouter()
    const [isSearchOpen, setIsSearchOpen] = useRecoilState(isSearchBoxOpenAtom)
    const [isNotificationsOpen, setIsNotificationsOpen] = useRecoilState(
        isNotificationsModalVisibleAtom
    )
    const [isQueueOpen, setIsQueueOpen] = useRecoilState(
        isNotificationQueueModalVisibleAtom
    )
    const isFirstLoad = useRef(true)

    const currentModal = useMemo((): ModalQueryValue | null => {
        if (isQueueOpen) return 'queue'
        if (isNotificationsOpen) return 'notifications'
        if (isSearchOpen) return 'search'
        return null
    }, [isSearchOpen, isNotificationsOpen, isQueueOpen])

    // URL → atoms (handles browser back / forward)
    useEffect(() => {
        const urlModal = (router.query.modal as string) ?? null
        if (urlModal !== currentModal) {
            setIsSearchOpen(urlModal === 'search')
            setIsNotificationsOpen(urlModal === 'notifications')
            setIsQueueOpen(urlModal === 'queue')
        }
    }, [router.query])

    // atoms → URL (handles programmatic open / close)
    useEffect(() => {
        if (isFirstLoad.current) {
            isFirstLoad.current = false
            return
        }
        const urlModal = (router.query.modal as string) ?? null
        if (currentModal === urlModal) return

        const query = { ...router.query }
        if (currentModal) {
            query.modal = currentModal
        } else {
            delete query.modal
        }
        router.push({ pathname: router.pathname, query })
    }, [currentModal])
}
